import { useEffect, useState, forwardRef } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { useMyContext } from '../../components/Provider/CommonProvider';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box
} from '@mui/material';
import { styled } from '@mui/system';

const StatsCard = styled(Card)(({ theme }) => ({
    minWidth: 200,
    height: '100%',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-5px)'
    }
}));

const StatsCardContent = styled(CardContent)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    '& .MuiTypography-h5': {
        fontWeight: 600,
        marginBottom: theme.spacing(1)
    },
    '& .MuiTypography-body1': {
        color: '#eeeee'
    }
}));

const BandwidthAnalytics = forwardRef(({ rtmpKey },ref) => {
    const baseURL = process.env.REACT_APP_Baseurl;
    const analyticsApi = `${baseURL}/admin/playout/bandwidth-analytics/${rtmpKey}`;

    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { fetchRequest } = useMyContext();

    const fetchData = async () => {
        try {
            setLoading(true);
            const result = await fetchRequest({
                method: 'GET',
                url: `${analyticsApi}`,
                data: {}
            });

            if (result?.status) {
                setAnalytics(result?.analytics);
            }
        } catch (error) {
            console.error(error?.response?.message);
            setError(error?.response?.message || 'Failed to fetch analytics data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // Poll every 10 seconds for updates
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [rtmpKey]);



    if (!analytics) return null;

    const timeSeriesData = {
        labels: Array.from({ length: 10 }, (_, i) => `${Math.floor(i * (analytics.durationSeconds / 10))}s`),
        datasets: [
            {
                label: 'Bandwidth (Mbps)',
                data: Array.from({ length: 10 }, (_, i) => {
                    // Simulate some variation - replace with real data if available
                    const base = parseFloat(analytics.bandwidthMbps);
                    return (base * 0.8) + (Math.random() * base * 0.4);
                }),
                borderColor: '#4e73df',
                backgroundColor: 'rgba(78, 115, 223, 0.05)',
                borderWidth: 2,
                pointRadius: 3,
                pointBackgroundColor: '#4e73df',
                pointBorderColor: '#fff',
                pointHoverRadius: 5,
                fill: true
            }
        ]
    };

    // Viewer data
    const viewerData = {
        labels: ['Current Viewers'],
        datasets: [
            {
                label: 'Viewers',
                data: [analytics.concurrentViewers],
                backgroundColor: '#1cc88a',
                borderColor: '#1cc88a',
                borderWidth: 1
            }
        ]
    };

    // Resolution distribution data for pie chart
    const resolutionChartData = {
        labels: analytics.resolutions.map(r => r.resolution),
        datasets: [
            {
                data: analytics.resolutions.map(r => r.percentage),
                backgroundColor: [
                    '#4e73df',
                    '#1cc88a',
                    '#36b9cc'
                ],
                hoverBackgroundColor: [
                    '#2e59d9',
                    '#17a673',
                    '#2c9faf'
                ],
                hoverBorderColor: "rgba(234, 236, 244, 1)",
            }
        ]
    };

    return (
        <div ref={ref} style={{ padding: '20px' }}>
            <Grid container spacing={3} style={{ marginBottom: '20px' }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <StatsCardContent>
                            <Typography variant="body1">Total Data</Typography>
                            <Typography variant="h5">{(analytics.totalBytes / (1024 * 1024)).toFixed(2)} MB</Typography>
                        </StatsCardContent>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <StatsCardContent>
                            <Typography variant="body1">Duration</Typography>
                            <Typography variant="h5">{analytics.durationSeconds} sec</Typography>
                        </StatsCardContent>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <StatsCardContent>
                            <Typography variant="body1">Bandwidth</Typography>
                            <Typography variant="h5">{analytics.bandwidthMbps} Mbps</Typography>
                        </StatsCardContent>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <StatsCardContent>
                            <Typography variant="body1">Current Viewers</Typography>
                            <Typography variant="h5">{analytics.concurrentViewers}</Typography>
                        </StatsCardContent>
                    </StatsCard>
                </Grid>
            </Grid>

            <Grid container spacing={3} style={{ marginBottom: '20px' }}>
                <Grid item xs={12} md={8}>
                    <Card style={{ padding: '20px', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Bandwidth Over Time</Typography>
                        <Line
                            data={timeSeriesData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        title: {
                                            display: true,
                                            text: 'Mbps'
                                        }
                                    },
                                    x: {
                                        title: {
                                            display: true,
                                            text: 'Time (seconds)'
                                        }
                                    }
                                }
                            }}
                        />
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card style={{ padding: '20px', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Viewer Statistics</Typography>
                        <Bar
                            data={viewerData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: true,
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            stepSize: 1
                                        }
                                    }
                                }
                            }}
                        />
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3} style={{ marginBottom: '20px' }}>
                <Grid item xs={12} md={6}>
                    <Card style={{ padding: '20px', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Resolution Distribution</Typography>
                        <Box height={300}>
                            <Pie
                                data={resolutionChartData}
                                options={{
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            position: 'bottom'
                                        },
                                        tooltip: {
                                            callbacks: {
                                                label: function (context) {
                                                    return `${context.label}: ${context.raw}%`;
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card style={{ padding: '20px', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Stream Quality Details</Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Resolution</TableCell>
                                        <TableCell align="right">Requests</TableCell>
                                        <TableCell align="right">Bandwidth</TableCell>
                                        <TableCell align="right">Percentage</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analytics.resolutions.map((row) => (
                                        <TableRow key={row.resolution}>
                                            <TableCell component="th" scope="row">
                                                {row.resolution}
                                            </TableCell>
                                            <TableCell align="right">{row.requests}</TableCell>
                                            <TableCell align="right">{row.bandwidth}</TableCell>
                                            <TableCell align="right">
                                                {row.percentage}%
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
});

export default BandwidthAnalytics;